import { cn } from '@respectly/ui';
import { Minus, ChevronUp, ArrowUp, Flame } from 'lucide-react';
import type { SupportPriority } from '@respectly/shared-types';

interface SupportPriorityBadgeProps {
  priority: SupportPriority;
  className?: string;
  showDot?: boolean;
  size?: 'sm' | 'md';
}

const priorityConfig: Record<
  SupportPriority,
  { label: string; icon: React.ElementType; classes: string; pulse?: boolean }
> = {
  low: {
    label: 'Low',
    icon: Minus,
    classes:
      'bg-zinc-500/10 text-zinc-500 dark:text-zinc-400 ring-1 ring-inset ring-zinc-500/20',
  },
  medium: {
    label: 'Medium',
    icon: ChevronUp,
    classes:
      'bg-blue-500/10 text-blue-600 dark:text-blue-400 ring-1 ring-inset ring-blue-500/25',
  },
  high: {
    label: 'High',
    icon: ArrowUp,
    classes:
      'bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-1 ring-inset ring-amber-500/25',
  },
  urgent: {
    label: 'Urgent',
    icon: Flame,
    classes:
      'bg-red-500/10 text-red-600 dark:text-red-400 ring-1 ring-inset ring-red-500/25',
    pulse: true,
  },
};

export function SupportPriorityBadge({
  priority,
  className,
  size = 'sm',
}: SupportPriorityBadgeProps) {
  const config = priorityConfig[priority] ?? priorityConfig.low;
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full font-medium',
        config.classes,
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        className
      )}
    >
      <Icon
        className={cn(
          size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3',
          config.pulse && 'animate-pulse'
        )}
      />
      {config.label}
    </span>
  );
}

export function getSupportPriorityColor(priority: SupportPriority): string {
  const colors: Record<SupportPriority, string> = {
    low: '#71717a',
    medium: '#3b82f6',
    high: '#f59e0b',
    urgent: '#ef4444',
  };
  return colors[priority];
}

export function getSupportPriorityLabel(priority: SupportPriority): string {
  return priorityConfig[priority].label;
}

export function getPrioritySortOrder(priority: SupportPriority): number {
  const order: Record<SupportPriority, number> = {
    low: 1,
    medium: 2,
    high: 3,
    urgent: 4,
  };
  return order[priority];
}
